import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Reveal, SectionTag } from "./Reveal";
import { Field, FilePicker, inputCls } from "./QuoteSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NetworkDiagram() {
  const suppliersY = [70, 180, 290];
  const customersY = [70, 180, 290];
  return (
    <svg viewBox="0 0 800 360" className="w-full h-auto" data-testid="supplier-network-diagram" role="img" aria-label="Suppliers connect through Ajemi to customers">
      <defs>
        <linearGradient id="pathGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.45" />
          <stop offset="50%" stopColor="#D9A441" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#00E5FF" stopOpacity="0.45" />
        </linearGradient>
      </defs>

      {suppliersY.map((y, i) => (
        <path key={`s${i}`} d={`M 98 ${y} C 240 ${y}, 250 180, 364 180`} fill="none" stroke="url(#pathGrad)" strokeWidth="1.5" strokeDasharray="5 9">
          <animate attributeName="stroke-dashoffset" from="0" to="-140" dur="5s" repeatCount="indefinite" />
        </path>
      ))}
      {customersY.map((y, i) => (
        <path key={`c${i}`} d={`M 436 180 C 550 180, 560 ${y}, 702 ${y}`} fill="none" stroke="url(#pathGrad)" strokeWidth="1.5" strokeDasharray="5 9">
          <animate attributeName="stroke-dashoffset" from="0" to="-140" dur="5s" repeatCount="indefinite" />
        </path>
      ))}

      <text x="70" y="26" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="10" letterSpacing="4">SUPPLIERS</text>
      <text x="730" y="26" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="10" letterSpacing="4">CUSTOMERS</text>

      {suppliersY.map((y, i) => (
        <g key={`sn${i}`}>
          <circle cx="70" cy={y} r="24" fill="#0A0D14" stroke="rgba(255,255,255,0.18)" />
          <circle cx="70" cy={y} r="24" fill="none" stroke="#00E5FF" strokeOpacity="0.4">
            <animate attributeName="r" values="24;36;24" dur="3.5s" begin={`${i * 0.7}s`} repeatCount="indefinite" />
            <animate attributeName="stroke-opacity" values="0.4;0;0.4" dur="3.5s" begin={`${i * 0.7}s`} repeatCount="indefinite" />
          </circle>
          <circle cx="70" cy={y} r="5" fill="#00E5FF" />
        </g>
      ))}
      {customersY.map((y, i) => (
        <g key={`cn${i}`}>
          <circle cx="730" cy={y} r="24" fill="#0A0D14" stroke="rgba(255,255,255,0.18)" />
          <circle cx="730" cy={y} r="24" fill="none" stroke="#00E5FF" strokeOpacity="0.4">
            <animate attributeName="r" values="24;36;24" dur="3.5s" begin={`${1.2 + i * 0.7}s`} repeatCount="indefinite" />
            <animate attributeName="stroke-opacity" values="0.4;0;0.4" dur="3.5s" begin={`${1.2 + i * 0.7}s`} repeatCount="indefinite" />
          </circle>
          <circle cx="730" cy={y} r="5" fill="#00E5FF" />
        </g>
      ))}

      <circle cx="400" cy="180" r="36" fill="#0A0D14" stroke="#D9A441" strokeWidth="1.5" />
      <circle cx="400" cy="180" r="36" fill="none" stroke="#D9A441" strokeOpacity="0.5">
        <animate attributeName="r" values="36;52;36" dur="4s" repeatCount="indefinite" />
        <animate attributeName="stroke-opacity" values="0.5;0;0.5" dur="4s" repeatCount="indefinite" />
      </circle>
      <text x="400" y="184" textAnchor="middle" fill="#F0C05A" fontSize="11" fontWeight="600" letterSpacing="2.5">AJEMI</text>
    </svg>
  );
}

const INITIAL = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  products_supplied: "",
  locations_served: "",
  available_volumes: "",
  website: "",
  business_info: "",
};

function SupplierForm({ onDone }) {
  const [form, setForm] = useState(INITIAL);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      const res = await axios.post(`${API}/supplier-applications`, fd);
      onDone(res.data.reference_id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Submission failed", {
        description: typeof detail === "string" ? detail : "Please check your details and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="supplier-form" className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
      <Field label="Company Name" required testId="supplier-field-company">
        <input required value={form.company_name} onChange={set("company_name")} className={inputCls} placeholder="Your company" data-testid="supplier-company-input" />
      </Field>
      <Field label="Contact Name" required testId="supplier-field-contact">
        <input required value={form.contact_name} onChange={set("contact_name")} className={inputCls} placeholder="Full name" data-testid="supplier-contact-input" />
      </Field>
      <Field label="Email" required testId="supplier-field-email">
        <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="name@company.com" data-testid="supplier-email-input" />
      </Field>
      <Field label="Phone" testId="supplier-field-phone">
        <input type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+234 ..." data-testid="supplier-phone-input" />
      </Field>
      <Field label="Products Supplied" required testId="supplier-field-products">
        <input required value={form.products_supplied} onChange={set("products_supplied")} className={inputCls} placeholder="e.g. Jet A-1, AGO, PMS" data-testid="supplier-products-input" />
      </Field>
      <Field label="Locations Served" testId="supplier-field-locations">
        <input value={form.locations_served} onChange={set("locations_served")} className={inputCls} placeholder="e.g. Lagos, Abuja, Port Harcourt" data-testid="supplier-locations-input" />
      </Field>
      <Field label="Available Volumes" testId="supplier-field-volumes">
        <input value={form.available_volumes} onChange={set("available_volumes")} className={inputCls} placeholder="e.g. 2M litres / month" data-testid="supplier-volumes-input" />
      </Field>
      <Field label="Company Website" testId="supplier-field-website">
        <input value={form.website} onChange={set("website")} className={inputCls} placeholder="https://" data-testid="supplier-website-input" />
      </Field>
      <div className="sm:col-span-2">
        <Field label="Business Information" testId="supplier-field-info">
          <textarea rows={4} value={form.business_info} onChange={set("business_info")} className={`${inputCls} resize-none`} placeholder="Registration, capacity, storage, logistics capability…" data-testid="supplier-info-input" />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <FilePicker files={files} setFiles={setFiles} testId="supplier-file-upload" />
      </div>
      <div className="sm:col-span-2 pt-1">
        <button
          type="submit"
          disabled={loading}
          data-testid="supplier-submit-btn"
          className="w-full inline-flex items-center justify-center gap-3 bg-gold text-ink font-outfit font-medium rounded-full px-8 py-4 hover:bg-goldbright hover:shadow-[0_0_40px_rgba(217,164,65,0.45)] transition-all duration-300 disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Submitting…
            </>
          ) : (
            <>
              Apply to Join Our Supply Network <ArrowUpRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export default function SupplierSection() {
  const [open, setOpen] = useState(false);
  const [referenceId, setReferenceId] = useState(null);

  return (
    <section id="supplier-network" data-testid="supplier-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_60%,rgba(0,229,255,0.05),transparent_60%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionTag number="13" label="Supplier Network" />
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-outfit text-3xl md:text-5xl font-extralight tracking-tight text-white max-w-xl">
              A trusted <span className="text-gold">supply network.</span>
            </h2>
            <p className="text-white/55 max-w-md text-base leading-relaxed">
              Ajemi works with selected supply partners to identify appropriate petroleum
              product sources — based on availability, location, commercial requirements and
              transaction considerations.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-14 glass-panel rounded-2xl p-6 md:p-10">
            <NetworkDiagram />
            <div className="mt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <p className="text-sm text-white/45 max-w-lg flex items-start gap-3">
                <ShieldCheck className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                Supplier identities are never publicly displayed. Every supply relationship is
                matched to the specific transaction.
              </p>
              <button
                data-testid="supplier-apply-btn"
                onClick={() => {
                  setReferenceId(null);
                  setOpen(true);
                }}
                className="group shrink-0 inline-flex items-center gap-3 border border-gold/50 text-gold font-outfit rounded-full pl-7 pr-2.5 py-2.5 hover:bg-gold hover:text-ink transition-all duration-300"
              >
                Become a Supply Partner
                <span className="bg-gold/15 rounded-full p-2 group-hover:bg-ink/10 group-hover:rotate-45 transition-all duration-300">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </button>
            </div>
          </div>
        </Reveal>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-surface border-white/10 text-white sm:max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid="supplier-dialog"
          data-lenis-prevent
        >
          {referenceId ? (
            <div className="text-center py-10" data-testid="supplier-success">
              <CheckCircle2 className="w-12 h-12 text-gold mx-auto" strokeWidth={1.2} />
              <h3 className="font-outfit text-2xl font-light text-white mt-5">Application received.</h3>
              <p className="text-white/60 mt-3 max-w-sm mx-auto text-sm leading-relaxed">
                Thank you for your interest in joining the Ajemi supply network. Our team will
                review your application and contact you.
              </p>
              <p className="mt-5 inline-block border border-gold/40 text-gold font-outfit tracking-[0.2em] text-xs rounded-full px-5 py-2" data-testid="supplier-reference-id">
                REF · {referenceId}
              </p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-outfit text-2xl font-light text-white">
                  Become a Supply Partner
                </DialogTitle>
                <DialogDescription className="text-white/50 text-sm">
                  Tell us about your supply capability. Applications are reviewed by the Ajemi
                  team before joining the network.
                </DialogDescription>
              </DialogHeader>
              <SupplierForm
                onDone={(ref) => setReferenceId(ref)}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
